import { type CSSProperties, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import { motion } from 'framer-motion';

import {
  currentLotterySeason,
  tournamentPrizePool,
} from '@/common/config/onboarding-config';
import { playSound } from '@/common/lib/soundEffect';
import { H1, TextMain } from '@/components/Typography/Typography';
import { MainButton } from '@/components/ui/Button/Button';
import { Picture } from '@/components/ui/Picture/Picture';
import { type OboardStatusChangeFunction } from '@/pages/Onboarding/model/Config';
import { changeOnboardingStatus } from '@/redux/reducers/userReducer';
import { useAppDispatch } from '@/redux/store';

import { getWelcomeLotteryImage } from './config';

import css from './styles.module.scss';

import tgemPng from '/img/tgem.png';
import tgemWebp from '/img/tgem.webp';

interface WelcomeLotteryProps {
  handleStatusChange: OboardStatusChangeFunction;
}

const WelcomeLottery: FC<WelcomeLotteryProps> = ({ handleStatusChange }) => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const mainPictureStyle = {
    '--welcome-lottery-image': `url("${getWelcomeLotteryImage(
      i18n.resolvedLanguage ?? i18n.language,
    )}")`,
  } as CSSProperties;
  const onClick = (): void => {
    dispatch(changeOnboardingStatus(false));
    playSound('buttons/switch');
    handleStatusChange('firstVipRegistration');
  };

  return (
    <motion.div
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className={css.welcomeLottery}
    >
      <div className={cn(css.bgEllipse, css.bgEllipseLeft)} />
      <div className={cn(css.bgEllipse, css.bgEllipseRight)} />
      <div className={cn(css.bgEllipse, css.bgEllipseGreen)} />
      <div className={css.mainPicture} style={mainPictureStyle}></div>
      <div className={css.season}>
        <span className={css.seasonText}>
          {t('season #{{seasonNumber}}', {
            seasonNumber: currentLotterySeason,
          })}
        </span>
      </div>
      <H1
        weight="extrabold"
        className="text-[40px] !leading-none uppercase mt-6"
      >
        {t('Prize Pool')}
      </H1>

      <div className="flex items-center gap-3">
        <Picture
          img={tgemPng}
          webp={tgemWebp}
          alt="t-gem"
          className="block size-[56px] shrink-0"
        />
        <span
          data-value={`${tournamentPrizePool.lottery}`}
          className={cn(css.rewardValue)}
        >
          <span>{`${tournamentPrizePool.lottery}`}</span>
        </span>
      </div>
      <TextMain className="max-w-[20.375rem] !leading-1.3 mt-[3.375rem] text-center">
        {t(
          'Ticket Draw: collect tickets, compete in the Arena and win rewards',
        )}
      </TextMain>
      <MainButton
        onClick={onClick}
        text={t("I'm in!")}
        className="mt-6 !w-[calc(100%_-_80px)] shrink-0"
      />
    </motion.div>
  );
};

export { WelcomeLottery };
